// src/pages/AdminSystemPage.jsx
// BSP System Health + Platform Monitoring + TTS Admin Controls
import React, { useState, useEffect } from 'react';
import AdminNav from '../components/AdminNav';
import AdminTopMenuBar from '../components/AdminTopMenuBar';
import AdminMobileBottomNav from '../components/AdminMobileBottomNav';
import UniversalFooter from '../components/UniversalFooter';
import { BACKEND_URL as backendUrl } from '@/config/api';
import { 
  Server, Activity, Database, AlertTriangle, Brain, 
  CheckCircle, Clock, XCircle, RefreshCw, Loader2,
  TrendingUp, FileText, Zap, ShieldAlert, Volume2, 
  StopCircle, Play, Trash2, Shield, Copy, Search
} from 'lucide-react';

// backendUrl imported from centralized config as BACKEND_URL

// Stat Card Component - moved outside main component
const StatCard = ({ icon: Icon, label, value, color, subtext }) => (
  <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
    <div className="flex items-center gap-3 mb-2">
      <Icon className={`w-5 h-5 ${color}`} />
      <span className="text-sm text-slate-400">{label}</span>
    </div>
    <div className="text-2xl font-bold text-white">{value}</div>
    {subtext && <div className="text-xs text-slate-500 mt-1">{subtext}</div>}
  </div>
);

// Job Status Badge Component - moved outside main component
const JobStatusBadge = ({ status, count }) => {
  const configs = {
    queued: { color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', icon: Clock },
    running: { color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: Loader2 },
    success: { color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', icon: CheckCircle },
    failed: { color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: XCircle }
  };
  const config = configs[status] || configs.queued;
  const IconComponent = config.icon;
  
  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${config.color}`}>
      <IconComponent className={`w-4 h-4 ${status === 'running' ? 'animate-spin' : ''}`} />
      <span className="capitalize">{status}</span>
      <span className="font-bold">{count}</span>
    </div>
  );
};

const AdminSystemPage = () => {
  const [bspStats, setBspStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  
  // TTS Admin State
  const [ttsJobs, setTtsJobs] = useState([]);
  const [ttsLoading, setTtsLoading] = useState(true);
  const [cancellingJob, setCancellingJob] = useState(null);
  const [triggeringRecovery, setTriggeringRecovery] = useState(false);

  // Deployment Verification State
  const [versionInfo, setVersionInfo] = useState(null);
  const [versionLoading, setVersionLoading] = useState(false);
  const [openCheckManuscriptId, setOpenCheckManuscriptId] = useState('');
  const [openCheckResult, setOpenCheckResult] = useState(null);
  const [openCheckLoading, setOpenCheckLoading] = useState(false);

  // Fetch deployment version info
  const fetchVersionInfo = async () => {
    setVersionLoading(true);
    try {
      const response = await fetch(`${backendUrl}/api/diagnostics/version`);
      if (response.ok) {
        const data = await response.json();
        setVersionInfo(data);
      } else {
        setVersionInfo({ error: `HTTP ${response.status}` });
      }
    } catch (error) {
      setVersionInfo({ error: error.message });
    } finally {
      setVersionLoading(false);
    }
  };

  // Run open-check diagnostic on a manuscript
  const runOpenCheck = async () => {
    if (!openCheckManuscriptId.trim()) {
      alert('Please enter a manuscript ID');
      return;
    }
    setOpenCheckLoading(true);
    setOpenCheckResult(null);
    try {
      const response = await fetch(`${backendUrl}/api/diagnostics/manuscripts/${openCheckManuscriptId.trim()}/open-check`);
      if (response.ok) {
        const data = await response.json();
        setOpenCheckResult(data);
      } else {
        const text = await response.text();
        setOpenCheckResult({ error: `HTTP ${response.status}`, raw: text });
      }
    } catch (error) {
      setOpenCheckResult({ error: error.message });
    } finally {
      setOpenCheckLoading(false);
    }
  };

  // Copy to clipboard helper
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  const fetchBspStats = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${backendUrl}/api/admin/bsp/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBspStats(data);
        setLastUpdated(new Date());
      }
    } catch (error) {
      console.error('Error fetching BSP stats:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Fetch stuck TTS jobs
  const fetchTtsJobs = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${backendUrl}/api/admin/tts/stuck-jobs?stale_minutes=5`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTtsJobs(data.stuck_jobs || []);
      }
    } catch (error) {
      console.error('Error fetching TTS jobs:', error);
    } finally {
      setTtsLoading(false);
    }
  };

  // Force cancel a TTS job
  const handleForceCancel = async (manuscriptId, title) => {
    if (!confirm(`Are you sure you want to FORCE CANCEL TTS for "${title}"?\n\nThis will stop all processing and mark the job as cancelled.`)) {
      return;
    }
    
    setCancellingJob(manuscriptId);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${backendUrl}/api/admin/tts/jobs/${manuscriptId}/force-cancel`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        alert(`✓ ${data.message}\n\nProgress at cancellation: ${data.progress_at_cancel}`);
        fetchTtsJobs(); // Refresh the list
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail || 'Failed to cancel job'}`);
      }
    } catch (error) {
      alert(`Error: ${error.message}`);
    } finally {
      setCancellingJob(null);
    }
  };

  // Trigger recovery for stuck jobs
  const handleTriggerRecovery = async () => {
    if (!confirm('Trigger TTS recovery for all stuck jobs?\n\nThis will attempt to restart processing for jobs that appear stuck.')) {
      return;
    }
    
    setTriggeringRecovery(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${backendUrl}/api/admin/tts/recovery/trigger`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        alert(`✓ Recovery triggered!\n\nRecovered: ${data.recovered_count || 0} jobs`);
        fetchTtsJobs();
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail || 'Failed to trigger recovery'}`);
      }
    } catch (error) {
      alert(`Error: ${error.message}`);
    } finally {
      setTriggeringRecovery(false);
    }
  };

  useEffect(() => {
    fetchBspStats();
    fetchTtsJobs();
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchBspStats(true);
      fetchTtsJobs();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50">
      <AdminNav />
      <AdminTopMenuBar />
      
      <div className="pb-20 md:pb-0">
        <main className="container mx-auto px-4 py-8 max-w-7xl">
          {/* Header */}
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-white mb-2">
                Admin – System
              </h1>
              <p className="text-slate-400">
                Monitor platform health and system intelligence
              </p>
            </div>
            <button
              onClick={() => fetchBspStats(true)}
              disabled={refreshing}
              className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg border border-slate-600 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          {/* Deployment Verification Section */}
          <section className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Shield className="w-6 h-6 text-emerald-400" />
              <h2 className="text-2xl font-bold text-white">Deployment Verification</h2>
              <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs rounded-full border border-emerald-500/30">
                Debug Tools
              </span>
            </div>
            
            <div className="bg-gradient-to-br from-emerald-900/20 to-slate-900/40 rounded-2xl border border-emerald-500/20 p-6 space-y-6">
              {/* Version Check */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Server className="w-5 h-5 text-emerald-400" />
                    Backend Version
                  </h3>
                  <button
                    onClick={fetchVersionInfo}
                    disabled={versionLoading}
                    className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors"
                    data-testid="check-version-btn"
                  >
                    {versionLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4" />
                    )}
                    Check Version
                  </button>
                </div>
                
                {versionInfo && (
                  <div className="bg-slate-800/60 rounded-lg p-4 border border-slate-700">
                    {versionInfo.error ? (
                      <div className="text-red-400 flex items-center gap-2">
                        <XCircle className="w-5 h-5" />
                        Error: {versionInfo.error}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          {versionInfo.ok ? (
                            <CheckCircle className="w-5 h-5 text-emerald-400" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-400" />
                          )}
                          <span className={`font-medium ${versionInfo.ok ? 'text-emerald-400' : 'text-red-400'}`}>
                            {versionInfo.env || 'Unknown Environment'}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                          <div>
                            <span className="text-slate-400">Build ID:</span>
                            <span className="ml-2 text-white font-mono">{versionInfo.build_id || 'N/A'}</span>
                          </div>
                          <div>
                            <span className="text-slate-400">Build Marker:</span>
                            <span className="ml-2 text-white font-mono">{versionInfo.build_marker || 'N/A'}</span>
                          </div>
                          <div>
                            <span className="text-slate-400">Git SHA:</span>
                            <span className="ml-2 text-white font-mono">{versionInfo.git_sha || 'N/A'}</span>
                          </div>
                          <div>
                            <span className="text-slate-400">Started:</span>
                            <span className="ml-2 text-white font-mono">{versionInfo.startup_time ? new Date(versionInfo.startup_time).toLocaleString() : 'N/A'}</span>
                          </div>
                        </div>
                        
                        {versionInfo.features && (
                          <div>
                            <span className="text-slate-400 text-sm">Features:</span>
                            <div className="flex flex-wrap gap-2 mt-2">
                              {versionInfo.features.map((feature, i) => (
                                <span key={i} className="px-2 py-1 bg-emerald-500/20 text-emerald-400 text-xs rounded border border-emerald-500/30">
                                  {feature}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        <button
                          onClick={() => copyToClipboard(JSON.stringify(versionInfo, null, 2))}
                          className="flex items-center gap-2 text-xs text-slate-400 hover:text-white transition-colors"
                        >
                          <Copy className="w-3 h-3" />
                          Copy JSON
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Open Check Diagnostic */}
              <div className="border-t border-emerald-500/20 pt-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Search className="w-5 h-5 text-emerald-400" />
                  Manuscript Open-Check Diagnostic
                </h3>
                <p className="text-sm text-slate-400 mb-4">
                  Test if a manuscript can be opened for playback. No authentication required.
                </p>
                
                <div className="flex gap-3 mb-4">
                  <input
                    type="text"
                    value={openCheckManuscriptId}
                    onChange={(e) => setOpenCheckManuscriptId(e.target.value)}
                    placeholder="Enter manuscript ID (e.g., 730c5d34-5fc9-487c-94d0-dd7e8c83617a)"
                    className="flex-1 px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
                    data-testid="manuscript-id-input"
                  />
                  <button
                    onClick={runOpenCheck}
                    disabled={openCheckLoading}
                    className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors"
                    data-testid="run-open-check-btn"
                  >
                    {openCheckLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Search className="w-4 h-4" />
                    )}
                    Run Check
                  </button>
                </div>
                
                {openCheckResult && (
                  <div className="bg-slate-800/60 rounded-lg p-4 border border-slate-700">
                    {openCheckResult.error ? (
                      <div className="text-red-400 flex items-center gap-2">
                        <XCircle className="w-5 h-5" />
                        Error: {openCheckResult.error}
                        {openCheckResult.raw && (
                          <pre className="mt-2 text-xs overflow-auto max-h-40">{openCheckResult.raw}</pre>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {/* Can Open Status */}
                        <div className="flex items-center gap-3">
                          {openCheckResult.can_open ? (
                            <>
                              <CheckCircle className="w-6 h-6 text-emerald-400" />
                              <span className="text-lg font-medium text-emerald-400">Can Open: YES</span>
                            </>
                          ) : (
                            <>
                              <XCircle className="w-6 h-6 text-red-400" />
                              <span className="text-lg font-medium text-red-400">Can Open: NO</span>
                            </>
                          )}
                        </div>
                        
                        {/* Blocking Reason */}
                        {openCheckResult.blocking_reason && (
                          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                            <span className="text-red-400 font-medium">Blocking Reason:</span>
                            <p className="text-red-300 font-mono text-sm mt-1">{openCheckResult.blocking_reason}</p>
                          </div>
                        )}
                        
                        {/* Checks */}
                        {openCheckResult.checks && (
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                            {Object.entries(openCheckResult.checks).map(([key, value]) => (
                              <div key={key} className="flex items-center gap-2">
                                {value === true ? (
                                  <CheckCircle className="w-4 h-4 text-emerald-400" />
                                ) : value === false ? (
                                  <XCircle className="w-4 h-4 text-red-400" />
                                ) : (
                                  <Clock className="w-4 h-4 text-slate-400" />
                                )}
                                <span className="text-slate-400">{key}:</span>
                                <span className={`font-mono ${value === true ? 'text-emerald-400' : value === false ? 'text-red-400' : 'text-white'}`}>
                                  {String(value)}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Trace ID */}
                        {openCheckResult.trace_id && (
                          <div className="text-xs text-slate-500">
                            Trace ID: <span className="font-mono">{openCheckResult.trace_id}</span>
                          </div>
                        )}
                        
                        <button
                          onClick={() => copyToClipboard(JSON.stringify(openCheckResult, null, 2))}
                          className="flex items-center gap-2 text-xs text-slate-400 hover:text-white transition-colors"
                        >
                          <Copy className="w-3 h-3" />
                          Copy Full JSON
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* BSP Intelligence Section */}
          <section className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Brain className="w-6 h-6 text-purple-400" />
              <h2 className="text-2xl font-bold text-white">BSP Intelligence System</h2>
              <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded-full border border-purple-500/30">
                Internal Only
              </span>
            </div>
            
            <p className="text-slate-400 text-sm mb-6">
              Book Semantic Profiles (BSP) power recommendations, similarity matching, and predictive analytics.
              BSP is a sealed, append-only analytical artifact derived from NFD.
            </p>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
              </div>
            ) : bspStats ? (
              <div className="space-y-6">
                {/* BSP Records Summary */}
                <div className="bg-gradient-to-br from-purple-900/20 to-slate-900/40 rounded-2xl border border-purple-500/20 p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Database className="w-5 h-5 text-purple-400" />
                    BSP Records
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <StatCard 
                      icon={FileText} 
                      label="Total BSP Records" 
                      value={bspStats.bsp_records?.total || 0}
                      color="text-purple-400"
                      subtext="All versions"
                    />
                    <StatCard 
                      icon={CheckCircle} 
                      label="Current BSPs" 
                      value={bspStats.bsp_records?.current || 0}
                      color="text-emerald-400"
                      subtext="Active profiles"
                    />
                    <StatCard 
                      icon={TrendingUp} 
                      label="With BSP" 
                      value={bspStats.manuscripts?.with_bsp || 0}
                      color="text-blue-400"
                      subtext="Manuscripts"
                    />
                    <StatCard 
                      icon={Clock} 
                      label="Pending BSP" 
                      value={bspStats.manuscripts?.pending_bsp || 0}
                      color="text-amber-400"
                      subtext="Awaiting generation"
                    />
                  </div>
                </div>

                {/* BSP Job Status */}
                <div className="bg-slate-800/30 rounded-2xl border border-slate-700 p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Zap className="w-5 h-5 text-amber-400" />
                    BSP Job Queue
                  </h3>
                  <div className="flex flex-wrap gap-3">
                    <JobStatusBadge status="queued" count={bspStats.jobs?.queued || 0} />
                    <JobStatusBadge status="running" count={bspStats.jobs?.running || 0} />
                    <JobStatusBadge status="success" count={bspStats.jobs?.success || 0} />
                    <JobStatusBadge status="failed" count={bspStats.jobs?.failed || 0} />
                  </div>
                  
                  {/* Health indicator */}
                  <div className="mt-4 pt-4 border-t border-slate-700">
                    {bspStats.jobs?.failed > 0 ? (
                      <div className="flex items-center gap-2 text-amber-400">
                        <AlertTriangle className="w-4 h-4" />
                        <span className="text-sm">{bspStats.jobs.failed} failed job(s) require attention</span>
                      </div>
                    ) : bspStats.jobs?.running > 0 ? (
                      <div className="flex items-center gap-2 text-blue-400">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span className="text-sm">BSP generation in progress...</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-emerald-400">
                        <CheckCircle className="w-4 h-4" />
                        <span className="text-sm">All BSP jobs complete - system healthy</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Governance Note */}
                <div className="bg-slate-900/50 rounded-xl border border-slate-700/50 p-4">
                  <p className="text-xs text-slate-500 italic">
                    BSP is a sealed, append-only analytical artifact derived exclusively from NFD.
                    It is generated asynchronously, versioned, and immutable once written.
                    BSP data may be observed by Admins but never modified by humans.
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-slate-400">
                Unable to load BSP statistics
              </div>
            )}

            {lastUpdated && (
              <p className="text-xs text-slate-500 mt-4">
                Last updated: {lastUpdated.toLocaleTimeString()}
              </p>
            )}
          </section>

          {/* Platform Health Section */}
          <section className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Server className="w-6 h-6 text-blue-400" />
              <h2 className="text-2xl font-bold text-white">Platform Health</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-slate-800/50 rounded-lg p-6 border border-slate-700/50">
                <Activity className="w-8 h-8 text-green-400 mb-3" />
                <h3 className="font-semibold text-white mb-2">System Health</h3>
                <p className="text-sm text-slate-400">
                  Monitor server status, uptime, and performance metrics
                </p>
                <div className="mt-3 flex items-center gap-2 text-emerald-400 text-sm">
                  <CheckCircle className="w-4 h-4" />
                  Operational
                </div>
              </div>

              <div className="bg-slate-800/50 rounded-lg p-6 border border-slate-700/50">
                <Database className="w-8 h-8 text-blue-400 mb-3" />
                <h3 className="font-semibold text-white mb-2">Database</h3>
                <p className="text-sm text-slate-400">
                  MongoDB connection status and statistics
                </p>
                <div className="mt-3 flex items-center gap-2 text-emerald-400 text-sm">
                  <CheckCircle className="w-4 h-4" />
                  Connected
                </div>
              </div>

              <div className="bg-slate-800/50 rounded-lg p-6 border border-slate-700/50">
                <AlertTriangle className="w-8 h-8 text-amber-400 mb-3" />
                <h3 className="font-semibold text-white mb-2">Error Logs</h3>
                <p className="text-sm text-slate-400">
                  Track errors, warnings, and system alerts
                </p>
                <div className="mt-3 text-slate-500 text-sm">
                  Coming soon
                </div>
              </div>
            </div>
          </section>

          {/* TTS Admin Controls Section */}
          <section className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Volume2 className="w-6 h-6 text-red-400" />
              <h2 className="text-2xl font-bold text-white">TTS Admin Controls</h2>
              <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-full border border-red-500/30">
                Kill Switch
              </span>
            </div>
            
            <div className="bg-gradient-to-br from-red-900/20 to-slate-900/40 rounded-2xl border border-red-500/20 p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <StopCircle className="w-5 h-5 text-red-400" />
                    Stuck/Running TTS Jobs
                  </h3>
                  <p className="text-sm text-slate-400 mt-1">
                    Jobs that have not updated in 5+ minutes. Use Force Cancel to stop hung processes.
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleTriggerRecovery}
                    disabled={triggeringRecovery}
                    className="flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors"
                  >
                    {triggeringRecovery ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Play className="w-4 h-4" />
                    )}
                    Trigger Recovery
                  </button>
                  <button
                    onClick={() => fetchTtsJobs()}
                    className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Refresh
                  </button>
                </div>
              </div>

              {ttsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-red-400" />
                </div>
              ) : ttsJobs.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <CheckCircle className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
                  <p>No stuck TTS jobs found</p>
                  <p className="text-xs text-slate-500 mt-1">All TTS processing is healthy</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {ttsJobs.map((job) => (
                    <div 
                      key={job.manuscript_id}
                      className="bg-slate-800/60 rounded-lg p-4 border border-slate-700"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium text-white">{job.title}</h4>
                          <p className="text-xs text-slate-400 mt-1">
                            ID: {job.manuscript_id}
                          </p>
                          <div className="flex flex-wrap gap-3 mt-2 text-sm">
                            <span className="text-amber-400">
                              Progress: {job.progress}
                            </span>
                            <span className="text-slate-400">
                              Status: <span className="text-red-400">{job.status}</span>
                            </span>
                            <span className="text-slate-400">
                              Stuck for: <span className="text-red-400">{job.stuck_minutes} min</span>
                            </span>
                          </div>
                          {job.last_error && (
                            <p className="text-xs text-red-400 mt-2">
                              Last error: {typeof job.last_error === 'string' ? job.last_error : job.last_error?.message || JSON.stringify(job.last_error)}
                            </p>
                          )}
                        </div>
                        <button
                          onClick={() => handleForceCancel(job.manuscript_id, job.title)}
                          disabled={cancellingJob === job.manuscript_id}
                          className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors ml-4"
                        >
                          {cancellingJob === job.manuscript_id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                          Force Cancel
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-4 pt-4 border-t border-red-500/20">
                <p className="text-xs text-red-400/70 italic flex items-center gap-2">
                  <ShieldAlert className="w-3 h-3" />
                  Force Cancel will permanently stop TTS processing. The job will NOT auto-resume.
                </p>
              </div>
            </div>
          </section>

          {/* Economic Reset Tool Section */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-amber-400" />
              <h2 className="text-2xl font-bold text-white">Economic Reset Tool</h2>
              <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded-full border border-amber-500/30">
                Epoch-Based
              </span>
            </div>
            
            <div className="bg-gradient-to-br from-amber-900/10 to-slate-900/40 rounded-2xl border border-amber-500/20 p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
                    <Database className="w-5 h-5 text-amber-400" />
                    Deterministic Testing Reset
                  </h3>
                  <p className="text-sm text-slate-400 mb-4 max-w-2xl">
                    Epoch-based economic reset for deterministic testing. Clears all financial 
                    and credit data while preserving user identities and catalog. Users will 
                    experience first login behavior (card capture required).
                  </p>
                  <ul className="text-xs text-slate-500 space-y-1 mb-4">
                    <li>• Clears: reader_credit_ledger, financial_transactions, billing_profiles</li>
                    <li>• Resets: membership_type to Free, subscription fields to null</li>
                    <li>• Preserves: User accounts, manuscripts, pricing, system settings</li>
                    <li>• Advances economic epoch to isolate test cycles</li>
                  </ul>
                </div>
                <a
                  href="/admin/system/reset"
                  className="flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors font-medium"
                >
                  <AlertTriangle className="w-4 h-4" />
                  Open Reset Tool
                </a>
              </div>
              
              <div className="mt-4 pt-4 border-t border-amber-500/20">
                <p className="text-xs text-amber-400/70 italic flex items-center gap-2">
                  <ShieldAlert className="w-3 h-3" />
                  Two-step confirmation required. Preview shows exact changes before execution.
                </p>
              </div>
            </div>
          </section>

          {/* AI Discovery Tools Section */}
          <section className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Brain className="w-6 h-6 text-cyan-400" />
              <h2 className="text-2xl font-bold text-white">AI Discovery & SEO</h2>
              <span className="px-2 py-0.5 bg-cyan-500/20 text-cyan-400 text-xs rounded-full border border-cyan-500/30">
                Public Pages
              </span>
            </div>
            
            <div className="bg-gradient-to-br from-cyan-900/10 to-slate-900/40 rounded-2xl border border-cyan-500/20 p-6">
              <p className="text-sm text-slate-400 mb-4">
                Public pages optimized for AI crawlers (LLMs), search engines, and semantic discovery. 
                These pages provide structured information about NewAudia without authentication requirements.
              </p>
              
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                <button
                  onClick={() => window.open("/ai", "_blank", "noopener,noreferrer")}
                  className="flex items-center gap-2 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors text-sm"
                >
                  <Brain className="w-4 h-4 text-cyan-400" />
                  AI Discovery
                </button>
                <button
                  onClick={() => window.open("/how-it-works", "_blank", "noopener,noreferrer")}
                  className="flex items-center gap-2 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors text-sm"
                >
                  <Zap className="w-4 h-4 text-amber-400" />
                  How It Works
                </button>
                <button
                  onClick={() => window.open("/why-newaudia", "_blank", "noopener,noreferrer")}
                  className="flex items-center gap-2 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors text-sm"
                >
                  <TrendingUp className="w-4 h-4 text-emerald-400" />
                  Why NewAudia
                </button>
                <button
                  onClick={() => window.open("/for-authors", "_blank", "noopener,noreferrer")}
                  className="flex items-center gap-2 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors text-sm"
                >
                  <FileText className="w-4 h-4 text-purple-400" />
                  For Authors
                </button>
                <button
                  onClick={() => window.open("/for-publishers", "_blank", "noopener,noreferrer")}
                  className="flex items-center gap-2 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors text-sm"
                >
                  <Database className="w-4 h-4 text-blue-400" />
                  For Publishers
                </button>
              </div>
              
              <div className="mt-4 pt-4 border-t border-cyan-500/20">
                <p className="text-xs text-slate-500 mb-2">Static Files (for crawlers):</p>
                <div className="flex flex-wrap gap-2">
                  <a 
                    href="/robots.txt" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-xs text-cyan-400 hover:text-cyan-300 underline"
                  >
                    /robots.txt
                  </a>
                  <a 
                    href="/sitemap.xml" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-xs text-cyan-400 hover:text-cyan-300 underline"
                  >
                    /sitemap.xml
                  </a>
                  <a 
                    href="/llms.txt" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-xs text-cyan-400 hover:text-cyan-300 underline"
                  >
                    /llms.txt
                  </a>
                </div>
              </div>
            </div>
          </section>
        </main>
      </div>

      <AdminMobileBottomNav />
      <UniversalFooter />
    </div>
  );
};

export default AdminSystemPage;
